import { useCallback, useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { NdaPdfDocument } from "../../../components/scoping/NdaPdfDocument";

export interface NdaPdfSource {
  id?: string | null;
  client_name?: string | null;
  nda_signer_name?: string | null;
  nda_signer_address?: string | null;
  nda_signature_data?: string | null;
  nda_signed_at?: string | null;
  nda_ss_signer_name?: string | null;
  nda_ss_signature_data?: string | null;
  nda_ss_signed_at?: string | null;
}

export function getNdaPdfFilename(clientName?: string | null): string {
  return `NDA_${(clientName || "client").replace(/[^a-z0-9]/gi, "_")}_signed.pdf`;
}

export function useNdaPdfUrl(source: NdaPdfSource | null | undefined) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!source?.nda_signature_data) {
      setPdfUrl(null);
      setIsLoading(false);
      return;
    }

    let active = true;
    let url: string | null = null;
    setIsLoading(true);

    const buildPdf = async () => {
      try {
        const docNode = (
          <NdaPdfDocument
            clientName={source.nda_signer_name || source.client_name || "Client"}
            clientAddress={source.nda_signer_address || ""}
            signerName={source.nda_signer_name || undefined}
            signerAddress={source.nda_signer_address || undefined}
            signatureBase64={source.nda_signature_data}
            signedAt={source.nda_signed_at}
            ssSignerName={source.nda_ss_signer_name}
            ssSignatureBase64={source.nda_ss_signature_data}
            ssSignedAt={source.nda_ss_signed_at}
            signerCompanyName={source.client_name || undefined}
            sessionId={source.id || undefined}
          />
        );
        const blob = await pdf(docNode).toBlob();
        if (active) {
          url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      } catch (err) {
        console.error("Error building NDA PDF:", err);
        if (active) {
          setPdfUrl(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    buildPdf();

    return () => {
      active = false;
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [
    source?.client_name,
    source?.nda_signer_name,
    source?.nda_signer_address,
    source?.nda_signature_data,
    source?.nda_signed_at,
    source?.nda_ss_signer_name,
    source?.nda_ss_signature_data,
    source?.nda_ss_signed_at,
    source?.id
  ]);

  const download = useCallback(() => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = getNdaPdfFilename(source?.client_name || source?.nda_signer_name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfUrl, source?.client_name, source?.nda_signer_name]);

  return {
    pdfUrl,
    isLoading,
    canDownload: !!pdfUrl,
    download
  };
}
